import { type ChangeEvent, type KeyboardEvent, type RefObject, useEffect } from 'react';
import styled from 'styled-components';

import { theme } from '../../common/ui/theme';

const TextArea = styled.textarea`
    background-color: ${theme.colors.background.default};
    border: none;
    box-sizing: border-box;
    color: ${theme.colors.foreground.default};
    font-family: inherit;
    font-size: 16px;
    line-height: 24px;
    min-height: 32px;
    outline: none;
    overflow: hidden;
    padding: 4px 8px;
    resize: none;
    width: 100%;

    &::placeholder {
        color: ${theme.colors.foreground.disabled};
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
    }
`;

const SCROLL_BOTTOM_BUFFER = 80;

interface ActivityInputTextAreaProps {
    inputRef: RefObject<HTMLTextAreaElement | null>;
    onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    placeholder: string;
    value: string;
}

export const ActivityInputTextArea = ({
    inputRef,
    onChange,
    onKeyDown,
    placeholder,
    value,
}: ActivityInputTextAreaProps) => {
    useEffect(() => {
        const input = inputRef.current;

        if (!input) {
            return;
        }

        input.style.height = 'auto';
        input.style.height = `${input.scrollHeight}px`;

        if (document.activeElement !== input) {
            return;
        }

        const inputBottom = input.getBoundingClientRect().bottom;
        const overflow = inputBottom + SCROLL_BOTTOM_BUFFER - window.innerHeight;

        if (overflow > 0) {
            window.scrollBy({ top: overflow });
        }
    }, [inputRef, value]);

    return (
        <TextArea ref={inputRef} onChange={onChange} value={value} placeholder={placeholder} onKeyDown={onKeyDown} />
    );
};
